import { useMemo } from 'react'

import StatusCodes from 'http-status-codes'
import { useQuery } from 'react-query'

import { usePagination } from '@webapp/core/hooks/usePagination'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { ExternalBankAccount } from '@webapp/platform/banking/entities/ExternalBankAccount'
import { externalBankAccountQueryConfig } from '../../../query-configs/externalBankAccount'

type UseQueryExternalBankAccountConfigsReturn = {
  accounts: ExternalBankAccount[]
  pagination?: RepoPaginationResponse
  isFetching: boolean
  isError: boolean
}

export const useQueryExternalBankAccountConfigs =
  (): UseQueryExternalBankAccountConfigsReturn => {
    const { params, updateNumPages, updateParams } = usePagination({
      id: 'bank-account-configs',
    })

    const { data, isFetching, isError } = useQuery({
      ...externalBankAccountQueryConfig.getExternalBankAccounts(params),
      onSuccess: (res) => {
        updateNumPages(res.pagination.numPages)
      },
      // The backend 404s a page past the end, e.g. after deleting the only
      // row on the last page; step back instead of stranding the user there.
      onError: (err) => {
        if (
          err instanceof RepoError &&
          err.status === StatusCodes.NOT_FOUND &&
          params.page > 1
        ) {
          updateParams({ page: params.page - 1 })
        }
      },
    })

    const accounts = useMemo(
      () =>
        (data?.data ?? []).map(
          (constructor) => new ExternalBankAccount(constructor)
        ),
      [data]
    )

    return {
      accounts,
      pagination: data?.pagination,
      isFetching,
      isError,
    }
  }
